import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/seo/SEO";
import { CTASection } from "@/components/CTASection";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Music, 
  Speaker, 
  Smartphone, 
  Settings, 
  ArrowRight, 
  CheckCircle, 
  Star,
  Headphones,
  Wifi,
  Volume2,
  Home,
  Zap
} from "lucide-react";
import { Link } from "react-router-dom";
import { track } from "@/lib/analytics";
import { RelatedServicesSection } from "@/components/RelatedServicesSection";

const audioProducts = [
  {
    id: "install-speaker-7-master",
    name: "Install Speaker 7 Master",
    description: "Actieve luidspreker met coaxiaal geïntegreerde tweeter. Kan tot 20 Client speakers aansturen via Master-Client Interface.",
    videoUrl: "https://pim.loxone.com//01%20Product%20Data/01%20Products/Audio/610149%20-%20Install%20Speaker%207%20Master/Images/vi-overview-610149-install-speaker-7-master.webm",
    features: [
      "30W RMS vermogen",
      "Frequentiebereik: 45Hz-20kHz",
      "Tot 20 Client speakers",
      "24V Tree voeding",
      "Coaxiale tweeter",
      "Direct Loxone aansturing"
    ],
    specifications: {
      power: "30W RMS",
      frequency: "45Hz-20kHz",
      clients: "Tot 20 speakers",
      voltage: "24V Tree",
      mounting: "Inbouw/plafond"
    }
  },
  {
    id: "wall-speaker-anthracite",
    name: "Wall Speaker Antraciet",
    description: "Elegante wandluidspreker in antraciet finish voor discrete integratie in moderne interieurs.",
    videoUrl: "https://pim.loxone.com//01%20Product%20Data/01%20Products/Audio/610001%20-%20WALL%20Speaker/Images/vi-overview-610001-wallspeaker-anthrazit.webm",
    features: [
      "Antraciet design",
      "Inbouw of opbouw montage",
      "Per ruimte zone controle",
      "App & Touch bediening",
      "Geluidsdemping",
      "Modern design"
    ],
    specifications: {
      design: "Antraciet",
      mounting: "Inbouw/opbouw",
      control: "App & Touch",
      zones: "Per ruimte",
      integration: "Volledige Loxone"
    }
  },
  {
    id: "wall-speaker-white",
    name: "Wall Speaker Wit",
    description: "Stijlvolle witte wandluidspreker die perfect integreert in lichte en moderne woninginrichtingen.",
    videoUrl: "https://pim.loxone.com//01%20Product%20Data/01%20Products/Audio/610141%20-%20WALL%20Speaker%20white/Images/vi-overview-610141-wallspeaker-white.webm",
    features: [
      "Wit design",
      "Minimalistisch uiterlijk",
      "Lichte interieurs",
      "Discrete integratie",
      "Hoogwaardige audio",
      "Eenvoudige installatie"
    ],
    specifications: {
      design: "Wit",
      style: "Minimalistisch",
      integration: "Discrete",
      audio: "Hi-Fi kwaliteit",
      installation: "Plug & Play"
    }
  },
  {
    id: "satellite-speaker-ip64-black",
    name: "Satellite Speaker IP64 Antraciet",
    description: "Weersbestendige buitenluidspreker voor terras, tuin en andere outdoor toepassingen.",
    videoUrl: "https://pim.loxone.com//01%20Product%20Data/01%20Products/Audio/610155%20-%20Satellite%20Speaker%20IP64%20Passive/Images/vi-overview-610155-satellite-speaker-IP64-anthrazit.webm",
    features: [
      "IP64 weerbestendig",
      "Outdoor toepassingen",
      "Antraciet finish",
      "UV-bestendig materiaal",
      "Terras & tuin audio",
      "Robuuste constructie"
    ],
    specifications: {
      protection: "IP64",
      location: "Outdoor",
      material: "UV-bestendig",
      color: "Antraciet",
      usage: "Terras/tuin"
    }
  },
  {
    id: "satellite-speaker-ip64-white",
    name: "Satellite Speaker IP64 Wit",
    description: "Witte weersbestendige luidspreker voor stijlvolle outdoor audio in moderne tuininrichtingen.",
    videoUrl: "https://pim.loxone.com//01%20Product%20Data/01%20Products/Audio/610174%20-%20Satellite%20Speaker%20IP64%20Passive%20wei%C3%9F/Images/vi-overview-610174-satellite-speaker-IP64-white.webm",
    features: [
      "IP64 weerbestendig",
      "Wit design",
      "Moderne tuinen",
      "Stijlvolle uitstraling",
      "Outdoor entertainment",
      "Duurzame afwerking"
    ],
    specifications: {
      protection: "IP64",
      design: "Wit",
      style: "Modern",
      application: "Outdoor",
      durability: "Weerbestendig"
    }
  }
];

const streamingServices = [
  { name: "Spotify", icon: Music },
  { name: "Apple Music", icon: Headphones },
  { name: "Amazon Music", icon: Volume2 },
  { name: "Deezer", icon: Music },
  { name: "Tidal", icon: Headphones },
  { name: "Radio streaming", icon: Wifi }
];

const audioFeatures = [
  {
    icon: Speaker,
    title: "Master-Client Architectuur",
    description: "Eén Master speaker stuurt tot 20 Client speakers aan via één kabel"
  },
  {
    icon: Music,
    title: "Alle Streaming Services",
    description: "Spotify, Apple Music, Amazon Music en meer direct geïntegreerd"
  },
  {
    icon: Smartphone,
    title: "App Controle",
    description: "Volledige bediening via Loxone app met zones en volume per ruimte"
  },
  {
    icon: Settings,
    title: "Automatische Scenes",
    description: "Muziek start automatisch bij thuiskomst of party mode activatie"
  },
  {
    icon: Home,
    title: "Multiroom Sync",
    description: "Synchrone muziek door het hele huis of verschillende zones apart"
  },
  {
    icon: Zap,
    title: "Energie Efficiënt",
    description: "Speakers gaan automatisch in slaapstand als er geen muziek speelt"
  }
];

const jsonLd = [
  {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Loxone Audio Systemen - Multiroom Audio Installatie",
    "description": "Professionele Loxone multiroom audio systemen met Master-Client architectuur. Install Speaker, Wall Speaker en Satellite Speaker voor binnen en buiten.",
    "provider": {
      "@type": "Organization",
      "name": "SOL-VI",
      "url": "https://sol-vi.nl"
    },
    "areaServed": [
      {
        "@type": "Place",
        "name": "Nederland"
      }
    ],
    "serviceType": "Audio System Installation",
    "url": "https://sol-vi.nl/audio",
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Loxone Audio Systeem Aanbiedingen",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Product",
            "name": "Loxone Install Speaker 7 Master",
            "description": "Actieve luidspreker met 30W RMS en Master-Client Interface voor tot 20 speakers"
          }
        },
        {
          "@type": "Offer", 
          "itemOffered": {
            "@type": "Product",
            "name": "Loxone Wall Speaker Collectie",
            "description": "Elegante wandluidsprekers in antraciet en wit voor discrete integratie"
          }
        }
      ]
    }
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Hoeveel speakers kan ik aansluiten op één Loxone Audio systeem?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Met de Master-Client architectuur kunt u tot 20 Client speakers aansluiten op één Install Speaker 7 Master. Voor grotere installaties kunnen meerdere Masters worden gebruikt."
        }
      },
      {
        "@type": "Question",
        "name": "Welke streaming diensten zijn compatibel met Loxone Audio?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Loxone Audio ondersteunt alle populaire streaming diensten zoals Spotify, Apple Music, Amazon Music, Deezer, Tidal en internetradio stations."
        }
      },
      {
        "@type": "Question",
        "name": "Kan ik verschillende muziek afspelen in verschillende ruimtes?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Ja, Loxone Audio biedt volledige multiroom functionaliteit. U kunt verschillende muziek afspelen per zone of alles synchroon laten lopen door het hele huis."
        }
      },
      {
        "@type": "Question",
        "name": "Zijn de Satellite Speakers geschikt voor buiten?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Ja, de Satellite Speaker IP64 modellen zijn speciaal ontworpen voor outdoor gebruik met IP64 bescherming tegen weer en wind."
        }
      }
    ]
  }
];

export default function Audio() {
  return (
    <>
      <SEO 
        title="Loxone Audio Systemen - Multiroom Audio Installatie | SOL-VI"
        description="Professionele Loxone multiroom audio systemen. Install Speaker 7 Master, Wall Speaker en Satellite IP64 speakers. Master-Client architectuur tot 20 speakers per keten."
        canonical="https://sol-vi.nl/audio"
        jsonLd={jsonLd}
        keywords="loxone audio, multiroom audio, install speaker 7 master, wall speaker loxone, satellite speaker ip64, smart home audio, streaming audio systeem, master client audio, loxone speakers, automatische audio"
      />
      
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-primary/10 to-primary/5 pt-32 pb-16">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                <div>
                  <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
                    <Star className="h-4 w-4" />
                    Loxone Partner
                  </div>
                  
                  <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
                    Professional <span className="text-primary">Audio</span> Systemen
                  </h1>
                  
                  <p className="text-xl text-muted-foreground mb-8 leading-relaxed">
                    Geniet van perfecte audio door het hele huis met Loxone's innovatieve 
                    Master-Client architectuur. Eén speaker stuurt tot 20 zones aan met 
                    kristalheldere geluidskwaliteit.
                  </p>
                  
                  <div className="flex flex-col sm:flex-row gap-4 mb-8">
                    <Button 
                      size="lg" 
                      className="text-lg px-8 py-4"
                      onClick={() => {
                        track("audio_hero_calendly");
                        (window as any).Calendly?.initPopupWidget?.({
                          url: 'https://calendly.com/sol-vi/new-meeting?primary_color=ff7800',
                          pageSettings: {
                            backgroundColor: 'ffffff',
                            hideEventTypeDetails: false,
                            hideLandingPageDetails: false,
                            primaryColor: 'ff7800',
                            textColor: '4d5055'
                          }
                        });
                      }}
                    >
                      Plan Audio Advies
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                    <Button size="lg" variant="outline" className="text-lg px-8 py-4" asChild>
                      <Link to="/smarthome">
                        <Music className="mr-2 h-5 w-5" />
                        Smart Home Overzicht
                      </Link>
                    </Button>
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4 text-center">
                    <div>
                      <div className="text-2xl font-bold text-primary">20</div>
                      <div className="text-sm text-muted-foreground">Speakers per keten</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-primary">∞</div>
                      <div className="text-sm text-muted-foreground">Streaming services</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-primary">1</div>
                      <div className="text-sm text-muted-foreground">Kabel per zone</div>
                    </div>
                  </div>
                </div>
                
                <div>
                  <Card className="overflow-hidden">
                    <div className="relative aspect-video bg-black">
                      <video 
                        autoPlay 
                        muted 
                        loop 
                        playsInline
                        className="w-full h-full object-cover"
                      >
                        <source src="https://pim.loxone.com/02%20Category%20Data/Audio/Master%20-%20Client%20webshop%20category.webm" type="video/webm" />
                        <div className="absolute inset-0 flex items-center justify-center text-white">
                          <div className="text-center">
                            <Music className="h-16 w-16 mx-auto mb-4" />
                            <p className="text-xl">Loxone Audio - Master-Client Systeem</p>
                          </div>
                        </div>
                      </video>
                      <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm rounded-full px-3 py-1">
                        <span className="text-sm font-medium text-primary">Multiroom Audio Overview</span>
                      </div>
                    </div>
                  </Card>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Audio Products Section */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold mb-4">
                  Loxone Audio <span className="text-primary">Product Range</span>
                </h2>
                <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                  Van binnen tot buiten, van woonkamer tot tuin - onze complete speaker collectie biedt voor elke ruimte de perfecte oplossing
                </p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {audioProducts.map((product, index) => (
                  <Card key={product.id} className="overflow-hidden group hover:shadow-lg transition-all">
                    <div className="relative aspect-video bg-black">
                      <video 
                        autoPlay 
                        muted 
                        loop 
                        playsInline
                        className="w-full h-full object-cover"
                      >
                        <source src={product.videoUrl} type="video/webm" />
                        <div className="absolute inset-0 flex items-center justify-center text-white">
                          <Speaker className="h-12 w-12" />
                        </div>
                      </video>
                      <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm rounded-full px-3 py-1">
                        <span className="text-sm font-medium text-primary">{product.name}</span>
                      </div>
                    </div>
                    
                    <CardHeader>
                      <CardTitle className="text-xl">{product.name}</CardTitle>
                      <CardDescription>{product.description}</CardDescription>
                    </CardHeader>
                    
                    <CardContent>
                      <div className="space-y-4">
                        <div>
                          <h4 className="font-semibold mb-2">Key Features:</h4>
                          <div className="space-y-1">
                            {product.features.slice(0, 3).map((feature, idx) => (
                              <div key={idx} className="flex items-center gap-2">
                                <CheckCircle className="h-4 w-4 text-primary flex-shrink-0" />
                                <span className="text-sm">{feature}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-2 text-xs bg-muted/50 p-3 rounded-lg">
                          {Object.entries(product.specifications).slice(0, 4).map(([key, value]) => (
                            <div key={key}>
                              <strong className="capitalize">{key}:</strong> {value}
                            </div>
                          ))}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Audio Features Section */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold mb-4">
                  Waarom Kiezen voor <span className="text-primary">Loxone Audio</span>?
                </h2>
                <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                  Ontdek de voordelen van een professioneel multiroom audio systeem dat perfect integreert met uw smart home
                </p>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {audioFeatures.map((feature, index) => (
                  <Card key={index} className="group hover:shadow-md transition-shadow">
                    <CardContent className="p-6">
                      <div className="bg-primary/10 w-12 h-12 rounded-xl flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                        <feature.icon className="h-6 w-6 text-primary" />
                      </div>
                      <h3 className="font-semibold text-lg mb-2">{feature.title}</h3>
                      <p className="text-muted-foreground text-sm">{feature.description}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Streaming Services Section */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Alle <span className="text-primary">Streaming Services</span> Geïntegreerd
              </h2>
              <p className="text-lg text-muted-foreground mb-12">
                Geniet direct van uw favoriete muziek zonder extra apps of complexe configuraties
              </p>
              
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
                {streamingServices.map((service, index) => (
                  <div key={index} className="flex flex-col items-center gap-3 p-4 rounded-lg hover:bg-muted/50 transition-colors">
                    <div className="bg-primary/10 w-16 h-16 rounded-xl flex items-center justify-center">
                      <service.icon className="h-8 w-8 text-primary" />
                    </div>
                    <span className="font-medium text-sm">{service.name}</span>
                  </div>
                ))}
              </div>
              
              <div className="mt-12 bg-gradient-to-r from-primary/5 to-primary/10 p-8 rounded-xl">
                <h3 className="text-xl font-bold mb-4">Nog meer mogelijkheden</h3>
                <p className="text-muted-foreground">
                  Internetradio, podcasts, eigen muziekcollectie en zelfs TV-geluid - alles is mogelijk met uw Loxone Audio systeem
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
                Veelgestelde <span className="text-primary">Vragen</span>
              </h2>
              
              <div className="space-y-6">
                <details className="group bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm">
                  <summary className="font-semibold text-lg cursor-pointer flex items-center justify-between">
                    Hoeveel speakers kan ik aansluiten op één Loxone Audio systeem?
                    <ArrowRight className="h-5 w-5 group-open:rotate-90 transition-transform" />
                  </summary>
                  <div className="mt-4 text-muted-foreground">
                    Met de Master-Client architectuur kunt u tot 20 Client speakers aansluiten op één Install Speaker 7 Master. 
                    Voor grotere installaties kunnen meerdere Masters worden gebruikt, waardoor u onbeperkt kunt uitbreiden.
                  </div>
                </details>

                <details className="group bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm">
                  <summary className="font-semibold text-lg cursor-pointer flex items-center justify-between">
                    Welke streaming diensten zijn compatibel met Loxone Audio?
                    <ArrowRight className="h-5 w-5 group-open:rotate-90 transition-transform" />
                  </summary>
                  <div className="mt-4 text-muted-foreground">
                    Loxone Audio ondersteunt alle populaire streaming diensten zoals Spotify, Apple Music, Amazon Music, 
                    Deezer, Tidal en duizenden internetradio stations. Ook uw eigen muziekcollectie is eenvoudig te integreren.
                  </div>
                </details>

                <details className="group bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm">
                  <summary className="font-semibold text-lg cursor-pointer flex items-center justify-between">
                    Kan ik verschillende muziek afspelen in verschillende ruimtes?
                    <ArrowRight className="h-5 w-5 group-open:rotate-90 transition-transform" />
                  </summary>
                  <div className="mt-4 text-muted-foreground">
                    Ja, Loxone Audio biedt volledige multiroom functionaliteit. U kunt verschillende muziek afspelen per zone, 
                    volume per ruimte regelen, of alles synchroon laten lopen door het hele huis voor feesten.
                  </div>
                </details>

                <details className="group bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm">
                  <summary className="font-semibold text-lg cursor-pointer flex items-center justify-between">
                    Zijn de Satellite Speakers geschikt voor buiten?
                    <ArrowRight className="h-5 w-5 group-open:rotate-90 transition-transform" />
                  </summary>
                  <div className="mt-4 text-muted-foreground">
                    Ja, de Satellite Speaker IP64 modellen zijn speciaal ontworpen voor outdoor gebruik. Ze hebben IP64 bescherming 
                    tegen weer en wind, en zijn gemaakt van UV-bestendige materialen voor jarenlange duurzaamheid.
                  </div>
                </details>

                <details className="group bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm">
                  <summary className="font-semibold text-lg cursor-pointer flex items-center justify-between">
                    Hoe eenvoudig is de installatie van Loxone Audio?
                    <ArrowRight className="h-5 w-5 group-open:rotate-90 transition-transform" />
                  </summary>
                  <div className="mt-4 text-muted-foreground">
                    Dankzij de Tree technologie is slechts één kabel per zone nodig. Als Loxone Partner zorgen wij voor 
                    professionele installatie, configuratie en uitleg zodat u direct kunt genieten van uw audio systeem.
                  </div>
                </details>
              </div>
            </div>
          </div>
        </section>

        <RelatedServicesSection 
          currentService="audio"
          title="Andere Smart Home Diensten"
          description="Maak uw audio systeem onderdeel van een compleet smart home ecosysteem"
        />

        <CTASection />
      </main>
      
      <Footer />
    </>
  );
}